import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QrCode, DownloadSimple, FileText, House, ForkKnife, Coffee, ArrowSquareOut, X, Copy, Code, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const FORMS = [
  { key: "checkin",   label: "Check-in",   icon: House,      color: "bg-emerald-600", height: 1650 },
  { key: "breakfast", label: "Frühstück",  icon: Coffee,     color: "bg-amber-600",   height: 1900 },
  { key: "dinner",    label: "Mittag/Abendessen", icon: ForkKnife, color: "bg-red-600", height: 1350 },
];

const buildIframeSnippet = (embedUrl, height) =>
`<!-- MainSpessartBlick Formular · automatisch eingebettet -->
<iframe
  src="${embedUrl}"
  title="MainSpessartBlick Formular"
  style="width:100%; max-width:800px; min-height:${height}px; border:0; display:block; margin:0 auto;"
  loading="lazy"
  allow="clipboard-write">
</iframe>
<script>
  // Automatische Höhen-Anpassung
  window.addEventListener("message", function(e){
    if (e && e.data && e.data.__msbFormHeight) {
      var f = document.querySelector('iframe[src="${embedUrl}"]');
      if (f) f.style.height = (e.data.__msbFormHeight + 40) + "px";
    }
  });
</script>`;

export default function FormsCenter() {
  const [counts, setCounts] = useState({});
  const [urls, setUrls]     = useState({});
  const [list, setList]     = useState(null);
  const [listType, setListType] = useState(null);
  const [detail, setDetail] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [snippet, setSnippet] = useState(null); // {label, code}
  // Custom-QR (freie URL, z. B. Homepage)
  const [customUrl, setCustomUrl] = useState("mainspessartblick.de");
  const [customQrKey, setCustomQrKey] = useState(0);
  const customQrSrc = customUrl.trim()
    ? `${API}/api/qr?url=${encodeURIComponent(customUrl.trim())}&v=${customQrKey}`
    : "";

  const reload = () => Promise.all([
    api.get("/forms/counts").then(r => setCounts(r.data)),
    api.get("/forms/urls").then(r => setUrls(r.data)),
  ]);
  useEffect(() => { reload(); }, []);

  const openList = async (ft) => {
    setListType(ft);
    const { data } = await api.get("/forms/submissions", { params: { form_type: ft }});
    setList(data.items);
  };
  const openDetail = async (item) => {
    setDetail(item);
    if (!item.read) {
      await api.post(`/forms/submissions/${item.id}/read`);
      reload(); openList(listType);
    }
  };
  const downloadQR = (ft) => window.open(`${API}/api/forms/qr/${ft}`, "_blank");
  const downloadCustomQR = () => {
    const u = customUrl.trim();
    if (!u) return;
    window.open(`${API}/api/qr?url=${encodeURIComponent(u)}`, "_blank");
  };
  const downloadPDF = (id) => window.open(`${API}/api/forms/submissions/${id}/pdf`, "_blank");
  const copy = (text, label = "Kopiert") => {
    navigator.clipboard.writeText(text); toast.success(label);
  };

  return (
    <div className="space-y-6" data-testid="forms-center">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Formulare · QR-Code Zentrale</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Drei öffentliche Formulare zum Einbinden bei Strato oder Ausdrucken als QR-Code. Klicke eine Kachel für die Liste aller Einsendungen.
          </p>
        </div>
        <Button variant="outline" onClick={()=>setHelpOpen(true)} className="rounded-xl" data-testid="strato-help-btn">
          <Question size={16} weight="bold"/><span className="ml-1.5">Strato-Anleitung</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {FORMS.map(f => {
          const c = counts[f.key] || { total: 0, unread: 0 };
          const Icon = f.icon;
          const url = urls[f.key]?.url || "";
          const embedUrl = urls[f.key]?.embed_url || "";
          return (
            <div key={f.key} className="msb-card overflow-hidden p-0" data-testid={`forms-tile-${f.key}`}>
              <button type="button" onClick={()=>openList(f.key)}
                      className={`${f.color} text-white p-5 w-full text-left hover:opacity-95 transition`}>
                <Icon size={32} weight="duotone" className="mb-2"/>
                <div className="font-bold text-lg">{f.label}</div>
                <div className="text-xs opacity-90 mt-0.5">Einsendungen · zum Öffnen klicken</div>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>{c.unread}</span>
                  <span className="text-xs opacity-90">neu · gesamt {c.total}</span>
                </div>
              </button>
              <div className="p-4 space-y-3 border-t border-border">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Öffentliche Strato-URL</div>
                  <div className="flex items-center gap-1 mt-1">
                    <code className="text-xs bg-muted rounded px-2 py-1 flex-1 truncate">{url}</code>
                    <Button variant="outline" size="sm" onClick={()=>copy(url, "URL kopiert")} className="rounded-lg" title="URL kopieren" data-testid={`copy-url-${f.key}`}>
                      <Copy size={12} weight="bold"/>
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Strato iframe-Snippet</div>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="text-xs bg-slate-900 text-slate-100 rounded px-2 py-1 flex-1 truncate font-mono">
                      &lt;iframe src="{embedUrl}" ... /&gt;
                    </div>
                    <Button variant="outline" size="sm" onClick={()=>setSnippet({label:f.label, code: buildIframeSnippet(embedUrl, f.height)})} className="rounded-lg" title="Snippet ansehen & kopieren" data-testid={`snippet-${f.key}`}>
                      <Code size={12} weight="bold"/>
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">QR-Code</div>
                  <div className="flex items-center gap-3">
                    <img src={`${API}/api/forms/qr/${f.key}`} alt={`QR ${f.label}`} className="w-24 h-24 rounded-lg border border-border" data-testid={`forms-qr-${f.key}`}/>
                    <Button variant="outline" size="sm" onClick={()=>downloadQR(f.key)} className="rounded-lg">
                      <DownloadSimple size={12} weight="bold"/><span className="ml-1">Herunterladen</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom QR-Generator (freie URL) */}
      <div className="msb-card" data-testid="custom-qr-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <QrCode size={22} weight="duotone" className="text-primary"/>
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Weitere QR-Codes</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Für beliebige URLs — z. B. deine Homepage. Ändere die URL und der QR passt sich sofort an.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Ziel-URL</label>
            <div className="flex gap-2 mt-1.5">
              <input
                type="text"
                value={customUrl}
                onChange={e => setCustomUrl(e.target.value)}
                placeholder="z. B. mainspessartblick.de"
                className="flex-1 h-10 border border-input rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="custom-qr-url"/>
              <Button variant="outline" onClick={()=>setCustomQrKey(v=>v+1)} className="rounded-xl" title="Aktualisieren" data-testid="custom-qr-refresh">
                Neu generieren
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Ohne <code>https://</code> wird es automatisch ergänzt.
            </div>
          </div>
          {customQrSrc && (
            <div className="flex items-center gap-3">
              <img src={customQrSrc} alt="Custom QR" className="w-28 h-28 rounded-lg border border-border" data-testid="custom-qr-image"/>
              <div className="flex flex-col gap-2">
                <Button onClick={downloadCustomQR} className="rounded-xl bg-primary gap-1.5" data-testid="custom-qr-download">
                  <DownloadSimple size={14} weight="bold"/> Herunterladen
                </Button>
                <Button variant="outline" onClick={()=>copy(customUrl.trim(), "URL kopiert")} className="rounded-xl gap-1.5">
                  <Copy size={12}/> URL kopieren
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Iframe-Snippet Dialog */}
      <Dialog open={snippet !== null} onOpenChange={(o)=>{if(!o) setSnippet(null);}}>
        <DialogContent className="max-w-2xl" data-testid="snippet-dialog">
          <DialogHeader><DialogTitle>Strato iframe-Snippet · {snippet?.label}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Kopiere den kompletten Code unten und füge ihn bei Strato in ein HTML-Element auf der Zielseite ein. Die Höhe passt sich automatisch an den Formular-Inhalt an.
          </p>
          <pre className="bg-slate-900 text-slate-100 rounded-xl p-4 text-xs overflow-x-auto max-h-[400px] font-mono whitespace-pre">
{snippet?.code}
          </pre>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=>setSnippet(null)} className="rounded-lg">Schließen</Button>
            <Button onClick={()=>{copy(snippet.code, "Snippet kopiert"); setSnippet(null);}} className="rounded-lg bg-primary text-white" data-testid="snippet-copy-btn">
              <Copy size={14} weight="bold"/><span className="ml-1.5">Snippet kopieren</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Strato-Anleitung Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="strato-help-dialog">
          <DialogHeader><DialogTitle>Anleitung: Formulare bei Strato einbinden</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto text-sm space-y-4 pr-1">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
              💡 Die Formulare liegen bereits live auf deinem Cockpit-Server und können sofort per iframe eingebunden werden — auch bevor bei Strato irgendetwas eingerichtet ist.
            </div>

            <section>
              <div className="font-bold text-slate-800 mb-1">1. Bei Strato einloggen und die Zielseite öffnen</div>
              <div className="text-muted-foreground">Melde dich unter <a href="https://www.strato.de/apps/CustomerService" target="_blank" rel="noopener" className="text-primary underline">strato.de/apps/CustomerService</a> an. Wechsle in den <b>Homepage-Baukasten</b> und öffne die Seite, die die neuen Formulare zeigen soll:</div>
              <ul className="list-disc pl-6 mt-1.5 space-y-0.5 text-muted-foreground">
                <li>Check-in: Seite <code className="bg-muted px-1.5 py-0.5 rounded">/check-in/</code></li>
                <li>Frühstück: Seite <code className="bg-muted px-1.5 py-0.5 rounded">/checkliste-fruehstueck/</code></li>
                <li>Essen: Seite <code className="bg-muted px-1.5 py-0.5 rounded">/mittag-und-abendessen/</code></li>
              </ul>
              <div className="text-muted-foreground mt-1">Existiert eine der Seiten noch nicht, lege sie mit <b>Seite hinzufügen → Leere Seite</b> an und benenne die URL wie oben.</div>
            </section>

            <section>
              <div className="font-bold text-slate-800 mb-1">2. Ein HTML-Element auf die Seite ziehen</div>
              <div className="text-muted-foreground">Ziehe aus der Elemente-Leiste ein <b>„HTML"</b>- oder <b>„Widget / Skript"</b>-Element auf die Seite (je nach Strato-Version heißt es „HTML-Snippet" oder „Eigener Code"). Es ist meist unter <i>Erweitert</i> zu finden.</div>
            </section>

            <section>
              <div className="font-bold text-slate-800 mb-1">3. Snippet einfügen</div>
              <div className="text-muted-foreground">In der Cockpit-Formular-Zentrale (diese Seite) auf das <b>Code-Icon &lt;/&gt;</b> in der Kachel klicken → <b>„Snippet kopieren"</b>. Zurück bei Strato den Code in das HTML-Element einfügen und speichern. Die Seite bei Strato veröffentlichen.</div>
            </section>

            <section>
              <div className="font-bold text-slate-800 mb-1">4. Fertig — testen</div>
              <div className="text-muted-foreground">Öffne im Browser <code className="bg-muted px-1.5 py-0.5 rounded">https://mainspessartblick.de/check-in/</code>. Das Formular sollte sofort erscheinen, Höhe passt sich automatisch an. Sende testweise eine Einsendung ab — sie erscheint hier im Cockpit als neu und wird per E-Mail an <b>info@mainspessartblick.de</b> geschickt.</div>
            </section>

            <section className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="font-bold text-slate-800 mb-1">Häufige Probleme</div>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground text-xs">
                <li><b>Weiße Seite / Formular fehlt:</b> Bei Strato die Seite <i>veröffentlichen</i> (nicht nur speichern). Manche Vorschau-Modi blocken iframes.</li>
                <li><b>Formular zu niedrig / gescrollt:</b> Das Skript im Snippet passt die Höhe automatisch an. Falls Strato Skripte blockiert, im Snippet einfach <code className="bg-muted px-1 rounded">min-height</code> manuell auf z.B. <code className="bg-muted px-1 rounded">2000px</code> erhöhen.</li>
                <li><b>QR-Code zeigt auf 404:</b> Die QR-Codes verweisen bereits auf die finalen Strato-URLs. Erst sichtbar, sobald die entsprechende Strato-Seite live ist.</li>
                <li><b>Signaturen funktionieren nicht per Finger:</b> Der iframe muss <code className="bg-muted px-1 rounded">allow="clipboard-write"</code> haben (bereits im Snippet enthalten).</li>
              </ul>
            </section>
          </div>
          <div className="pt-3 border-t border-border flex justify-end">
            <Button variant="outline" onClick={()=>setHelpOpen(false)} className="rounded-lg">
              <X size={14} weight="bold"/><span className="ml-1">Schließen</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Einsendungs-Liste als Dialog */}
      <Dialog open={list !== null} onOpenChange={(o)=>{if(!o) setList(null);}}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col" data-testid="forms-list-dialog">
          <DialogHeader><DialogTitle>Einsendungen · {FORMS.find(f=>f.key===listType)?.label}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {(list || []).length === 0
              ? <div className="text-sm text-muted-foreground py-6 text-center">Noch keine Einsendungen.</div>
              : <ul className="divide-y divide-border">
                  {(list||[]).map(row => (
                    <li key={row.id}>
                      <div className="w-full py-2.5 flex items-center gap-2 hover:bg-muted/50 rounded transition px-2">
                        <button onClick={()=>openDetail(row)} className="flex-1 text-left flex items-start gap-2 min-w-0" data-testid={`forms-row-${row.id}`}>
                          {!row.read && <span className="mt-1 inline-block w-2 h-2 bg-primary rounded-full flex-shrink-0"/>}
                          <div className="flex-1 min-w-0">
                            <div className={row.read ? "" : "font-bold"}>
                              {row.guest_name || "—"} {row.room_hint && <span className="text-muted-foreground font-normal">· {row.room_hint}</span>}
                            </div>
                            <div className="text-xs text-muted-foreground">{new Date(row.created_at).toLocaleString("de-DE")}</div>
                          </div>
                        </button>
                        <Button variant="outline" size="sm" onClick={()=>downloadPDF(row.id)} className="rounded-lg flex-shrink-0">
                          <FileText size={12} weight="bold"/><span className="ml-1">PDF</span>
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail-Dialog */}
      <Dialog open={detail !== null} onOpenChange={(o)=>{if(!o) setDetail(null);}}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>{detail?.guest_name || "Einsendung"}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto text-sm space-y-1">
            {Object.entries(detail?.data || {}).map(([k,v]) => {
              if (!v || (Array.isArray(v) && v.length===0)) return null;
              const isSignature = typeof v === "string" && v.startsWith("data:image");
              return (
                <div key={k} className="grid grid-cols-3 gap-3 py-1 border-b border-dashed border-border">
                  <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">{k.replace(/_/g," ")}</div>
                  <div className="col-span-2">
                    {isSignature
                      ? <img src={v} alt="Unterschrift" className="max-h-24 border border-border rounded bg-white p-1" data-testid="detail-signature"/>
                      : (Array.isArray(v) ? v.join(", ") : String(v))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-3 border-t border-border flex justify-between">
            <Button variant="outline" size="sm" onClick={()=>detail && downloadPDF(detail.id)} className="rounded-lg">
              <FileText size={14} weight="bold"/><span className="ml-1">PDF öffnen</span>
            </Button>
            <Button variant="outline" size="sm" onClick={()=>setDetail(null)} className="rounded-lg">
              <X size={14} weight="bold"/><span className="ml-1">Schließen</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
